import type { FC } from 'react'
import { Tooltip } from '@/components/ui/tooltip'
import { useTranslation } from '@/translations/i18n'
import { useLuxeFeatureFlag } from '../queries/use-get-luxe-feature-flags'
import { getVvScoreBadgeStyle, resolveDisplayScore } from '../utils'

interface FinalScoreBadgeProps {
	/** Gated by luxe-vv2-final-score-display: null with the flag off. */
	finalScore?: number | null
	designScore: number | null
	blockersFailedCount?: number | null
	hasScoreFallback?: boolean
	/** Off where the breakdown is already on the page, as on the detail pages. */
	hasBreakdownTooltip?: boolean
}

/**
 * The score badge in a queue row. Which of the two scores it shows is the
 * flag's call, so the badge reads the flag itself rather than making every
 * queue resolve the same thing and pass it down. The Final Score alone does not
 * say why it landed where it did, so hovering it breaks the number back down
 * into the design score and the count of failed blockers that cut it.
 */
export const FinalScoreBadge: FC<FinalScoreBadgeProps> = ({
	finalScore,
	designScore,
	blockersFailedCount,
	hasScoreFallback = true,
	hasBreakdownTooltip = true
}) => {
	const { t } = useTranslation()
	const { isEnabled: isFinalScoreEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-final-score-display'
	)

	const score = resolveDisplayScore({
		designScore,
		finalScore,
		isFinalScoreEnabled
	})

	if (score == null) {
		return hasScoreFallback ? (
			<span className='text-neutral-500 text-sm'>—</span>
		) : null
	}

	const badge = (
		<span
			className={`inline-flex rounded-full border px-2 py-0.5 font-medium text-xs ${getVvScoreBadgeStyle(score, isFinalScoreEnabled)}`}
		>
			{score}
		</span>
	)

	// With the flag off the badge already shows the design score, and a breakdown
	// of a number against itself explains nothing.
	if (!(hasBreakdownTooltip && isFinalScoreEnabled) || designScore == null) {
		return badge
	}

	return (
		<Tooltip
			content={
				<div className='flex flex-col gap-0.5'>
					<span>
						{t('luxe_submissions:final_score_tooltip_design', {
							score: designScore
						})}
					</span>

					<span>
						{t('luxe_submissions:final_score_tooltip_blockers', {
							blockers:
								blockersFailedCount !== undefined &&
								blockersFailedCount !== null
									? blockersFailedCount
									: '-'
						})}
					</span>
				</div>
			}
			triggerProps={{
				className: 'cursor-pointer'
			}}
		>
			{badge}
		</Tooltip>
	)
}
